import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AgentChatUser } from '@/components/agent/chat/types';

// Agent type that has organization_id
type Agent = {
  id: string;
  organization_id: string;
  [key: string]: any; // Allow other agent properties
};

declare global {
  interface Window {
    GLOBAL_PRE_SESSION_FIELDS?: Record<string, any>;
  }
}

type AgentChatUserData = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  pre_session_fields?: Record<string, any> | null;
};

export const useAgentChatUser = (
  isPublic: boolean,
  agentChatUserData: AgentChatUserData | null,
  agent: Agent | null
) => {
  const [agentChatUser, setAgentChatUser] = useState<AgentChatUser | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeAgentChatUser = async () => {
      // Only proceed if public chat and we have user data and agent and not already initialized
      if (!isPublic || !agentChatUserData || !agent || isInitialized) return;
      
      // Attempt to recover pre_session_fields if missing
      if (!agentChatUserData.pre_session_fields || 
          (typeof agentChatUserData.pre_session_fields === 'object' && 
           Object.keys(agentChatUserData.pre_session_fields || {}).length === 0)) {
        
        // Try to recover from global variable
        if (window.GLOBAL_PRE_SESSION_FIELDS && 
            Object.keys(window.GLOBAL_PRE_SESSION_FIELDS).length > 0) {
          agentChatUserData.pre_session_fields = window.GLOBAL_PRE_SESSION_FIELDS;
        } 
        // Try sessionStorage
        else if (sessionStorage.getItem('GLOBAL_PRE_SESSION_FIELDS')) {
          try {
            const restored = JSON.parse(sessionStorage.getItem('GLOBAL_PRE_SESSION_FIELDS') || '{}');
            agentChatUserData.pre_session_fields = restored;
          } catch (e) {
            console.error("Error parsing sessionStorage data:", e);
          }
        } 
        // Try direct localStorage
        else if (localStorage.getItem('DIRECT_PRE_SESSION_FIELDS')) {
          try {
            const restored = JSON.parse(localStorage.getItem('DIRECT_PRE_SESSION_FIELDS') || '{}');
            agentChatUserData.pre_session_fields = restored;
          } catch (e) {
            console.error("Error parsing localStorage data:", e);
          }
        }
      }
      
      try {
        // If email is provided, look for existing user with that email AND organization
        if (agentChatUserData.email) {
          const { data: existingUsers, error: queryError } = await supabase
            .from('agent_chat_users')
            .select('*')
            .eq('email', agentChatUserData.email)
            .eq('organization_id', agent.organization_id) // Filter by organization
            .limit(1);
            
          if (queryError) {
            console.error("Error fetching existing user:", queryError);
          }
            
          if (existingUsers && existingUsers.length > 0) {
            // Cast the database response to match AgentChatUser type
            const existingUser: AgentChatUser = {
              ...existingUsers[0],
              pre_session_fields: existingUsers[0].pre_session_fields as Record<string, any> | null
            };
            setAgentChatUser(existingUser);
            
            // Update user with any new information
            const updateData: Record<string, any> = { 
              updated_at: new Date().toISOString() 
            };
            
            if (agentChatUserData.name && existingUsers[0].name !== agentChatUserData.name) {
              updateData.name = agentChatUserData.name;
            }
            
            // Update phone if provided
            if (agentChatUserData.phone && existingUsers[0].phone !== agentChatUserData.phone) {
              updateData.phone = agentChatUserData.phone;
            }
            
            // Add pre_session_fields to updateData if the DB record doesn't have them but we do
            if (agentChatUserData.pre_session_fields && 
                Object.keys(agentChatUserData.pre_session_fields).length > 0 && 
                (!existingUsers[0].pre_session_fields || 
                 JSON.stringify(existingUsers[0].pre_session_fields) === '{}')) {
              updateData.pre_session_fields = agentChatUserData.pre_session_fields;
            }
            
            // Only perform update if we have changes
            if (Object.keys(updateData).length > 1) { // More than just updated_at
              const { error: updateError } = await supabase
                .from('agent_chat_users')
                .update(updateData)
                .eq('id', existingUsers[0].id);
                
              if (updateError) {
                console.error("Error updating user:", updateError);
              }
            }
            
            setIsInitialized(true);
            return;
          }
        }
        
        // Only create a new user if we have at least some data (name or email)
        if (agentChatUserData.name || agentChatUserData.email) {
          const newUserData = {
            name: agentChatUserData.name || null,
            email: agentChatUserData.email || null,
            phone: agentChatUserData.phone || null,
            organization_id: agent.organization_id, // Include organization_id for new users
            pre_session_fields: agentChatUserData.pre_session_fields || {},
            is_anonymous: !agentChatUserData.email // Mark as anonymous if no email provided
          };
          
          const { data: newUser, error } = await supabase
            .from('agent_chat_users')
            .insert(newUserData)
            .select()
            .single();
            
          if (error) {
            console.error("Error creating agent chat user:", error);
            throw error;
          }
          
          // Cast the database response to match AgentChatUser type
          const typedNewUser: AgentChatUser = {
            ...newUser,
            pre_session_fields: newUser.pre_session_fields as Record<string, any> | null
          };
          setAgentChatUser(typedNewUser);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error("Error initializing agent chat user:", error);
        setIsInitialized(true);
      }
    };
    
    initializeAgentChatUser();
  }, [isPublic, agentChatUserData, agent, isInitialized]);

  return { agentChatUser, setAgentChatUser };
};
